// client/src/coms/views/MapsView.js
import React, { useEffect, useMemo, useState } from 'react';
import logo from '../../assets/logo128x128.png';

const IS_DEV = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
const API_BASE = IS_DEV ? 'https://beta.point-atlas.io' : '';
const SITE_BASE = IS_DEV ? 'https://beta.point-atlas.io' : '';
const LS_VOX_MAPS_VIEW_STATE = 'vox:mapsViewState';

function resolveUploadsUrl(src) {
  if (!src) return '';
  const s = String(src).trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('/')) {
    if (IS_DEV) return `https://beta.point-atlas.io${s}`;
    return s;
  }
  if (IS_DEV) return `https://beta.point-atlas.io/${s}`;
  return `/${s}`;
}

function capitalise(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function slugifyName(name) {
  const raw = name == null ? '' : String(name);
  const trimmed = raw.trim();
  const base = trimmed.length ? trimmed : 'Map';
  const s = base
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s/g, '-')
    .replace(/[^\w\-]+/g, '-')
    .replace(/\-+/g, '-')
    .replace(/^\-+|\-+$/g, '');
  return s || 'Map';
}

function getDisplayName(map) {
  if (
    !map.name ||
    map.name.trim() === '' ||
    map.name.trim().toLowerCase() === map.game_name?.trim().toLowerCase()
  ) {
    return { main: capitalise(map.game_name), game: null };
  }
  return { main: map.name, game: capitalise(map.game_name) };
}

function getPrettyUrl(map) {
  const mode = (map.mode || '').toLowerCase();
  const isTabletopMode =
    mode === 'vtt' ||
    mode === 'dnd' ||
    mode === 'tabletop';

  const display = getDisplayName(map);
  const nameForSlug = map && map.name && String(map.name).trim() ? map.name : display.main;
  const slugName = slugifyName(nameForSlug);

  if (map && map.id != null) {
    const basePath = isTabletopMode ? '/vtt' : '/map';
    return `${basePath}/${encodeURIComponent(String(map.id))}/${encodeURIComponent(slugName)}`;
  }

  return '/map';
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function toInt(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

function pickMapId(map) {
  const id = map && map.id != null ? toInt(map.id, 0) : 0;
  return id > 0 ? id : 0;
}

function pickPublicFlag(map) {
  if (!map || typeof map !== 'object') return true;

  if (Object.prototype.hasOwnProperty.call(map, 'is_public')) {
    return Number(map.is_public) === 1;
  }

  if (Object.prototype.hasOwnProperty.call(map, 'public')) {
    return Number(map.public) === 1;
  }

  if (Object.prototype.hasOwnProperty.call(map, 'visibility')) {
    return String(map.visibility || '').trim().toLowerCase() === 'public';
  }

  return true;
}

function pickServerKeyFromProps(props) {
  const candidates = [
    props && props.serverKey,
    props && props.activeServerKey,
    props && props.currentServerKey,
    props && props.selectedServerKey,

    props && props.server && props.server.id,
    props && props.server && props.server.public_id,
    props && props.server && props.server.server_key,
    props && props.server && props.server.key,

    props && props.activeServer && props.activeServer.id,
    props && props.activeServer && props.activeServer.public_id,
    props && props.activeServer && props.activeServer.server_key,
    props && props.activeServer && props.activeServer.key,

    props && props.currentServer && props.currentServer.id,
    props && props.currentServer && props.currentServer.public_id,
    props && props.currentServer && props.currentServer.server_key,
    props && props.currentServer && props.currentServer.key,
  ];

  for (const candidate of candidates) {
    const s = String(candidate || '').trim();
    if (s) return s;
  }

  return '';
}

function isSpeechZoneBridgeMessage(data) {
  return !!(
    data &&
    typeof data === 'object' &&
    data.type === 'pa-local-speech-zone'
  );
}

async function fetchJson(url, opts) {
  const res = await fetch(url, opts);
  const text = await res.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg =
      (data && (data.error || data.detail || data.message)) ||
      `Request failed with status ${res.status}`;
    throw new Error(msg);
  }

  return data;
}

async function fetchMapById(mapId, serverKey) {
  const id = toInt(mapId, 0);
  const sk = String(serverKey || '').trim();
  if (!id) return null;

  const query = sk ? `?server_key=${encodeURIComponent(sk)}` : '';
  const candidates = [
    `${API_BASE}/api/maps/${id}${query}`,
    `${API_BASE}/api/map/${id}${query}`,
  ];

  for (const url of candidates) {
    try {
      const data = await fetchJson(url, { credentials: 'include' });
      if (data && typeof data === 'object') {
        if (data.map && typeof data.map === 'object') return data.map;
        return data;
      }
    } catch {}
  }

  return null;
}

function readStoredMapsViewState() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return { kind: 'list', mapId: 0 };
    const raw = String(window.localStorage.getItem(LS_VOX_MAPS_VIEW_STATE) || '').trim();
    if (!raw) return { kind: 'list', mapId: 0 };
    const parsed = JSON.parse(raw);
    const kind = parsed && parsed.kind === 'map' ? 'map' : 'list';
    const mapId = toInt(parsed && parsed.mapId, 0);
    return { kind, mapId };
  } catch {
    return { kind: 'list', mapId: 0 };
  }
}

function writeStoredMapsViewState(nextState) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const payload = {
      kind: nextState && nextState.kind === 'map' ? 'map' : 'list',
      mapId: toInt(nextState && nextState.mapId, 0),
    };
    window.localStorage.setItem(LS_VOX_MAPS_VIEW_STATE, JSON.stringify(payload));
  } catch {}
}

function VoxemeraMapCard({
  map,
  user,
  votes,
  userVote,
  onVote,
  isFavourited,
  favLoading,
  onFavourite,
  onOpen,
  badge,
}) {
  const display = getDisplayName(map);

  const thumbSrc = useMemo(() => {
    const src =
      map.thumb_url ||
      map.thumbnail_url ||
      map.image_url ||
      map.default_map_url ||
      map.image;
    return src ? resolveUploadsUrl(src) : '/offline-placeholder.png';
  }, [map]);

  function handleCardOpen() {
    if (typeof onOpen === 'function') onOpen(map);
  }

  return (
    <div
      className="map-thumbnail"
      onClick={handleCardOpen}
      style={{
        minHeight: 290,
        maxWidth: 245,
        marginBottom: 8,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      {badge ? (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 2,
            background: badge.kind === 'linked' ? 'rgba(var(--vx-accent-rgb, 88,101,242), 0.95)' : 'rgba(46, 204, 113, 0.92)',
            color: 'var(--vx-on-accent, #fff)',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.04em',
            padding: '5px 8px',
            borderRadius: 999,
            boxShadow: '0 4px 16px rgba(0,0,0,0.28)',
          }}
        >
          {badge.label}
        </div>
      ) : null}

      <img
        src={thumbSrc}
        alt={display.main}
        className="map-thumb-img"
        loading="lazy"
        decoding="async"
        style={{
          objectFit: 'cover',
          background: 'var(--vxs-4, #222)',
          width: '100%',
          height: 120,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      />

      <div
        className="map-name"
        style={{
          fontWeight: 700,
          fontSize: '1.12em',
          padding: '12px 0 4px 0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {display.main}
        {display.game && (
          <span
            style={{
              color: 'var(--vx-text-2, #aaa)',
              fontSize: '0.98em',
              fontWeight: 'normal',
              marginLeft: 7,
            }}
          >
            ({display.game})
          </span>
        )}
      </div>

      <div className="map-votes" onClick={(e) => e.stopPropagation()}>
        <button
          className={`map-vote-btn${userVote === 1 ? ' active' : ''}`}
          onClick={onVote ? () => onVote(map.id, userVote === 1 ? 0 : 1) : undefined}
          title="Vote up"
          disabled={!onVote || !user}
          tabIndex={-1}
        >
          👍
        </button>
        <span style={{ fontSize: 17 }}>
          {typeof votes === 'number' ? votes : map.votes || 0}
        </span>
        <button
          className={`map-vote-btn${userVote === -1 ? ' active' : ''}`}
          onClick={onVote ? () => onVote(map.id, userVote === -1 ? 0 : -1) : undefined}
          title="Vote down"
          disabled={!onVote || !user}
          tabIndex={-1}
        >
          👎
        </button>
      </div>

      {map.uploader_id && map.uploader_username ? (
        <div className="map-owner">by {map.uploader_username}</div>
      ) : null}

      <button
        className={`map-fav-btn${isFavourited ? ' favourited' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          if (onFavourite) onFavourite();
        }}
        disabled={favLoading || !user}
        title={isFavourited ? 'Remove from favourites' : 'Add to favourites'}
      >
        <span style={{ fontSize: 21, marginRight: 6 }}>
          {isFavourited ? '★' : '☆'}
        </span>
        {isFavourited ? 'Favourited' : 'Add to Favourites'}
      </button>

      <div
        style={{
          marginTop: 'auto',
          padding: '0 14px 12px 14px',
          display: 'flex',
          justifyContent: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleCardOpen}
          style={{
            width: '100%',
            border: 'none',
            borderRadius: 8,
            background: 'var(--vx-accent, #5865f2)',
            color: 'var(--vx-on-accent, #fff)',
            fontWeight: 700,
            fontSize: 14,
            padding: '9px 12px',
            cursor: 'pointer',
          }}
        >
          Open in Voxemera
        </button>
      </div>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          color: 'var(--vx-text, #fff)',
          fontWeight: 800,
          fontSize: 20,
        }}
      >
        {title}
      </div>
      {subtitle ? (
        <div
          style={{
            color: 'var(--vx-text-2, #b8c0cc)',
            fontSize: 14,
          }}
        >
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}

function MapsGrid({
  maps,
  user,
  votesData,
  favMapIds,
  favLoading,
  onVote,
  onFavourite,
  onOpen,
  badge,
}) {
  return (
    <div
      className="maps-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(245px, 245px))',
        gap: 18,
        justifyContent: 'center',
        alignItems: 'start',
      }}
    >
      {maps.map((map) => (
        <VoxemeraMapCard
          key={map.id}
          map={map}
          user={user}
          votes={votesData[map.id]?.total_votes}
          userVote={votesData[map.id]?.user_vote}
          onVote={(id, vote) => onVote(id, vote)}
          isFavourited={favMapIds.has(map.id)}
          favLoading={favLoading[map.id]}
          onFavourite={() => onFavourite(map.id, favMapIds.has(map.id))}
          onOpen={(chosenMap) => onOpen(chosenMap)}
          badge={badge}
        />
      ))}
    </div>
  );
}

export default function MapsView(props) {
  useEffect(() => {
    function handleSpeechZoneBridge(event) {
      const data = event && event.data;
      if (!isSpeechZoneBridgeMessage(data)) return;

      try {
        window.dispatchEvent(
          new CustomEvent('pa-local-speech-zone', {
            detail: {
              zone: data.zone ?? null,
            },
          })
        );
      } catch {}
    }

    window.addEventListener('message', handleSpeechZoneBridge);

    return () => {
      window.removeEventListener('message', handleSpeechZoneBridge);
    };
  }, []);

  const { user } = props;

  const [publicMaps, setPublicMaps] = useState([]);
  const [linkedMapsById, setLinkedMapsById] = useState({});
  const [visibleVoiceChannels, setVisibleVoiceChannels] = useState([]);
  const [favouriteMaps, setFavouriteMaps] = useState([]);
  const [votesData, setVotesData] = useState({});
  const [favLoading, setFavLoading] = useState({});
  const [loadingMaps, setLoadingMaps] = useState(true);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingLinkedMaps, setLoadingLinkedMaps] = useState(false);
  const [selectedMap, setSelectedMap] = useState(null);
  const [hasRestoredInitialView, setHasRestoredInitialView] = useState(false);

  const activeServerKey = useMemo(() => pickServerKeyFromProps(props), [props]);

  useEffect(() => {
    let cancelled = false;

    async function loadPublicMaps() {
      setLoadingMaps(true);

      try {
        const data = await fetchJson(`${API_BASE}/api/maps?sort=votes`, {
          credentials: 'include',
        });

        if (!cancelled) {
          setPublicMaps(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setPublicMaps([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingMaps(false);
        }
      }
    }

    loadPublicMaps();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadVisibleVoiceChannels() {
      const serverKey = String(activeServerKey || '').trim();

      if (!serverKey) {
        setVisibleVoiceChannels([]);
        return;
      }

      setLoadingChannels(true);

      try {
        const data = await fetchJson(
          `${API_BASE}/api/coms/servers/${encodeURIComponent(serverKey)}/channels`,
          { credentials: 'include' }
        );

        if (!cancelled) {
          const nextVoiceChannels = safeArray(data && data.voice_channels).map((row) => ({
            ...row,
            id: toInt(row && row.id, 0),
            linked_map_id: row && row.linked_map_id != null ? toInt(row.linked_map_id, 0) : null,
          }));
          setVisibleVoiceChannels(nextVoiceChannels);
        }
      } catch {
        if (!cancelled) {
          setVisibleVoiceChannels([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingChannels(false);
        }
      }
    }

    loadVisibleVoiceChannels();

    return () => {
      cancelled = true;
    };
  }, [activeServerKey]);

  const linkedMapIds = useMemo(() => {
    const out = new Set();

    for (const channel of safeArray(visibleVoiceChannels)) {
      const id = toInt(channel && channel.linked_map_id, 0);
      if (id > 0) out.add(id);
    }

    return Array.from(out);
  }, [visibleVoiceChannels]);

  useEffect(() => {
    let cancelled = false;

    async function loadLinkedMaps() {
      const serverKey = String(activeServerKey || '').trim();

      if (!serverKey || !linkedMapIds.length) {
        setLinkedMapsById({});
        setLoadingLinkedMaps(false);
        return;
      }

      setLoadingLinkedMaps(true);

      const publicIdSet = new Set(
        safeArray(publicMaps)
          .map((map) => pickMapId(map))
          .filter((id) => id > 0)
      );

      const next = {};

      for (const mapId of linkedMapIds) {
        if (publicIdSet.has(mapId)) {
          const existingPublicMap = safeArray(publicMaps).find((map) => pickMapId(map) === mapId);
          if (existingPublicMap) {
            next[mapId] = existingPublicMap;
          }
          continue;
        }

        try {
          const linkedMap = await fetchMapById(mapId, serverKey);
          if (linkedMap && pickMapId(linkedMap) > 0) {
            next[pickMapId(linkedMap)] = linkedMap;
          }
        } catch {}
      }

      if (!cancelled) {
        setLinkedMapsById(next);
        setLoadingLinkedMaps(false);
      }
    }

    loadLinkedMaps();

    return () => {
      cancelled = true;
    };
  }, [activeServerKey, linkedMapIds, publicMaps]);

  useEffect(() => {
    let cancelled = false;

    async function loadFavourites() {
      if (!user) {
        setFavouriteMaps([]);
        return;
      }

      try {
        const data = await fetchJson(`${API_BASE}/api/user/favourites`, {
          credentials: 'include',
        });

        if (!cancelled) {
          setFavouriteMaps(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setFavouriteMaps([]);
        }
      }
    }

    loadFavourites();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const allKnownMaps = useMemo(() => {
    const merged = new Map();

    for (const map of safeArray(publicMaps)) {
      const id = pickMapId(map);
      if (id > 0) merged.set(id, map);
    }

    for (const [key, map] of Object.entries(linkedMapsById || {})) {
      const id = toInt(key, 0);
      if (id > 0 && map) merged.set(id, map);
    }

    return Array.from(merged.values());
  }, [publicMaps, linkedMapsById]);

  const mapsForVoting = useMemo(() => {
    return safeArray(allKnownMaps).filter((map) => !map?.parent_map_id);
  }, [allKnownMaps]);

  useEffect(() => {
    let cancelled = false;

    async function loadVotes() {
      if (!mapsForVoting.length) {
        setVotesData({});
        return;
      }

      const nextVotesData = {};

      for (const map of mapsForVoting) {
        try {
          const data = await fetchJson(`${API_BASE}/api/map-votes/${map.id}/${user?.id || 0}`, {
            credentials: 'include',
          });
          nextVotesData[map.id] = data;
        } catch {
          nextVotesData[map.id] = { total_votes: map.votes || 0, user_vote: 0 };
        }
      }

      if (!cancelled) {
        setVotesData(nextVotesData);
      }
    }

    loadVotes();

    return () => {
      cancelled = true;
    };
  }, [mapsForVoting, user]);

  useEffect(() => {
    if (hasRestoredInitialView) return;
    if (loadingMaps || loadingChannels || loadingLinkedMaps) return;

    const stored = readStoredMapsViewState();

    if (stored.kind === 'map' && stored.mapId) {
      const restoredMap = allKnownMaps.find((map) => pickMapId(map) === stored.mapId) || null;
      if (restoredMap) {
        setSelectedMap(restoredMap);
      }
    }

    setHasRestoredInitialView(true);
  }, [hasRestoredInitialView, loadingMaps, loadingChannels, loadingLinkedMaps, allKnownMaps]);

  useEffect(() => {
    if (!hasRestoredInitialView) return;

    if (selectedMap && pickMapId(selectedMap) > 0) {
      writeStoredMapsViewState({ kind: 'map', mapId: pickMapId(selectedMap) });
      return;
    }

    writeStoredMapsViewState({ kind: 'list', mapId: 0 });
  }, [selectedMap, hasRestoredInitialView]);

  const handleVote = async (mapId, vote) => {
    if (!user) return;

    try {
      await fetch(`${API_BASE}/api/map-votes/${mapId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, vote }),
      });

      const data = await fetchJson(`${API_BASE}/api/map-votes/${mapId}/${user.id}`, {
        credentials: 'include',
      });

      setVotesData((prev) => ({ ...prev, [mapId]: data }));
    } catch {}
  };

  const handleFavourite = async (mapId, isFavourited) => {
    if (!user) return;

    setFavLoading((prev) => ({ ...prev, [mapId]: true }));

    try {
      await fetch(`${API_BASE}/api/user/favourites/${mapId}`, {
        method: isFavourited ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await fetchJson(`${API_BASE}/api/user/favourites`, {
        credentials: 'include',
      });

      setFavouriteMaps(Array.isArray(data) ? data : []);
    } catch {}

    setFavLoading((prev) => ({ ...prev, [mapId]: false }));
  };

  const favMapIds = useMemo(() => {
    return new Set(favouriteMaps.map((m) => m.id));
  }, [favouriteMaps]);

  const sortedRootMaps = useMemo(() => {
    const filtered = safeArray(allKnownMaps).filter((map) => !map?.parent_map_id);

    return [...filtered].sort((a, b) => {
      const aVotes = votesData[a.id]?.total_votes ?? a.votes ?? 0;
      const bVotes = votesData[b.id]?.total_votes ?? b.votes ?? 0;

      if (bVotes !== aVotes) return bVotes - aVotes;

      const aCreated = new Date(a.createdAt || a.created_at || 0).getTime();
      const bCreated = new Date(b.createdAt || b.created_at || 0).getTime();
      return bCreated - aCreated;
    });
  }, [allKnownMaps, votesData]);

  const linkedMaps = useMemo(() => {
    const idSet = new Set(linkedMapIds);
    return sortedRootMaps.filter((map) => idSet.has(pickMapId(map)));
  }, [sortedRootMaps, linkedMapIds]);

  const renderablePublicMaps = useMemo(() => {
    const linkedIdSet = new Set(linkedMapIds);
    return sortedRootMaps.filter((map) => pickPublicFlag(map) && !linkedIdSet.has(pickMapId(map)));
  }, [sortedRootMaps, linkedMapIds]);

  const loading = loadingMaps || loadingChannels || loadingLinkedMaps;
  const hasLinkedMaps = linkedMaps.length > 0;
  const hasPublicMaps = renderablePublicMaps.length > 0;
  const hasAnyMaps = hasLinkedMaps || hasPublicMaps;

  const selectedMapUrl = useMemo(() => {
    if (!selectedMap) return '';
    return `${SITE_BASE}${getPrettyUrl(selectedMap)}`;
  }, [selectedMap]);

  if (selectedMap) {
    const display = getDisplayName(selectedMap);

    return (
      <div
        className="coms-view maps-view-scroll"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          padding: 12,
          boxSizing: 'border-box',
          gap: 12,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(var(--vx-accent-rgb, 122,134,255), 0.72) rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
        }}
      >
        <style>{`
          .maps-view-scroll::-webkit-scrollbar {
            width: 12px;
            height: 12px;
          }
          .maps-view-scroll::-webkit-scrollbar-track {
            background: rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)));
            border-radius: 999px;
          }
          .maps-view-scroll::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, rgba(var(--vx-accent-rgb, 88,101,242), 0.95), rgba(var(--vx-accent-rgb, 122,134,255), 0.72));
            border-radius: 999px;
            border: 2px solid rgba(var(--vxs-1-rgb, 15,17,23), 0.85);
          }
          .maps-view-scroll::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, rgba(104,116,255,1), rgba(145,155,255,0.9));
          }
          .maps-view-scroll::-webkit-scrollbar-corner {
            background: transparent;
          }
        `}</style>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: 'rgba(var(--vxs-2-rgb, 20,23,31), 0.9)',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
            borderRadius: 12,
            padding: '10px 12px',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={() => setSelectedMap(null)}
            style={{
              border: 'none',
              borderRadius: 8,
              background: 'var(--vxs-6, #2f3542)',
              color: 'var(--vx-text, #fff)',
              fontWeight: 700,
              padding: '8px 12px',
              cursor: 'pointer',
            }}
          >
            ← Back
          </button>

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                color: 'var(--vx-text, #fff)',
                fontWeight: 800,
                fontSize: 16,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {display.main}
            </div>
            <div
              style={{
                color: 'var(--vx-text-2, #b8c0cc)',
                fontSize: 13,
              }}
            >
              Opened inside Voxemera
            </div>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            borderRadius: 14,
            overflow: 'hidden',
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
            background: 'var(--vxs-1, #0f1117)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.28)',
          }}
        >
          <iframe
            title={display.main || 'Map viewer'}
            src={selectedMapUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              background: 'var(--vxs-1, #0f1117)',
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="coms-view homepage maps-view-scroll"
      style={{
        padding: '18px 18px 28px',
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: '100%',
        boxSizing: 'border-box',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(var(--vx-accent-rgb, 122,134,255), 0.72) rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
      }}
    >
      <style>{`
        .maps-view-scroll::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }
        .maps-view-scroll::-webkit-scrollbar-track {
          background: rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)));
          border-radius: 999px;
        }
        .maps-view-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, rgba(var(--vx-accent-rgb, 88,101,242), 0.95), rgba(var(--vx-accent-rgb, 122,134,255), 0.72));
          border-radius: 999px;
          border: 2px solid rgba(var(--vxs-1-rgb, 15,17,23), 0.85);
        }
        .maps-view-scroll::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, rgba(104,116,255,1), rgba(145,155,255,0.9));
        }
        .maps-view-scroll::-webkit-scrollbar-corner {
          background: transparent;
        }
      `}</style>

      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: 18,
          marginBottom: 10,
          userSelect: 'none',
        }}
      >
        <img
          src={logo}
          alt="Voxemera maps"
          style={{
            width: 70,
            height: 70,
            marginBottom: 5,
            filter: 'drop-shadow(0 0 18px #2227)',
          }}
        />
        <h1
          className="brand"
          style={{
            fontSize: '2.7rem',
            fontWeight: 800,
            letterSpacing: '0.09em',
            margin: 0,
            color: 'var(--vx-text-2, #f8f9fa)',
            textShadow: '0 2px 14px #0009, 0 0px 1px #003',
            textAlign: 'center',
          }}
        >
          POINT ATLAS
        </h1>
        <p
          style={{
            marginTop: 10,
            marginBottom: 4,
            maxWidth: 820,
            textAlign: 'center',
            fontSize: 15,
            color: 'var(--vx-text-2, #d1d5db)',
          }}
        >
          Browse maps inside Voxemera. Linked maps you can access appear first, with public maps underneath.
        </p>
      </div>

      {loading ? (
        <div
          style={{
            color: 'var(--vx-text, #fff)',
            background: 'var(--vxs-4, #23252b)',
            padding: '28px 22px',
            borderRadius: 10,
            textAlign: 'center',
            marginTop: 30,
            marginBottom: 24,
            boxShadow: '0 4px 28px #0003',
          }}
        >
          Loading maps...
        </div>
      ) : !hasAnyMaps ? (
        <div
          style={{
            color: 'var(--vx-text, #fff)',
            background: 'var(--vxs-4, #23252b)',
            padding: '28px 22px',
            borderRadius: 10,
            textAlign: 'center',
            marginTop: 30,
            marginBottom: 24,
            boxShadow: '0 4px 28px #0003',
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 14 }}>
            No maps found.
          </div>
          <div style={{ fontSize: 16 }}>
            Maps available to this user will appear here.
          </div>
        </div>
      ) : (
        <>
          {hasLinkedMaps ? (
            <div className="section" style={{ width: '100%', marginBottom: 24 }}>
              <SectionHeader
                title="Linked Maps"
              />

              <MapsGrid
                maps={linkedMaps}
                user={user}
                votesData={votesData}
                favMapIds={favMapIds}
                favLoading={favLoading}
                onVote={handleVote}
                onFavourite={handleFavourite}
                onOpen={(chosenMap) => setSelectedMap(chosenMap)}
                badge={{ kind: 'linked', label: 'LINKED' }}
              />
            </div>
          ) : null}

          <div
            style={{
              width: '100%',
              height: 1,
              background: 'linear-gradient(90deg, rgba(var(--vx-fg-rgb, 255,255,255), calc(0 * var(--vx-ov, 1))), rgba(var(--vx-fg-rgb, 255,255,255), calc(0.18 * var(--vx-ov, 1))), rgba(var(--vx-fg-rgb, 255,255,255), calc(0 * var(--vx-ov, 1))))',
              margin: hasLinkedMaps ? '2px 0 22px 0' : '0 0 22px 0',
            }}
          />

          <div className="section" style={{ width: '100%' }}>
            <SectionHeader
              title="Public Maps"
              subtitle={
                hasLinkedMaps
                  ? null
                  : 'No linked maps available for the voice channels you can see, so public maps are shown here.'
              }
            />

            {hasPublicMaps ? (
              <MapsGrid
                maps={renderablePublicMaps}
                user={user}
                votesData={votesData}
                favMapIds={favMapIds}
                favLoading={favLoading}
                onVote={handleVote}
                onFavourite={handleFavourite}
                onOpen={(chosenMap) => setSelectedMap(chosenMap)}
                badge={{ kind: 'public', label: 'PUBLIC' }}
              />
            ) : (
              <div
                style={{
                  color: 'var(--vx-text, #fff)',
                  background: 'var(--vxs-4, #23252b)',
                  padding: '22px 18px',
                  borderRadius: 10,
                  textAlign: 'center',
                  marginTop: 8,
                  boxShadow: '0 4px 28px #0003',
                }}
              >
                No public maps available.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}